import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../auth/AuthContext';
import AuthPanel from './AuthPanel';
import AuthModal from './AuthModal';
import ShareExperienceFlow from './ShareExperienceFlow';

const Container = styled.div`
  min-height: 100vh;
  padding: 24px;
  max-width: 640px;
  margin: 0 auto;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #ff00aa, #00f5ff);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 28px;
  font-size: 0.95rem;
`;

const Message = styled.div`
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.95rem;
  margin-top: 8px;
`;

const BlockedMessage = styled(Message)`
  background: rgba(255, 180, 0, 0.15);
  border: 1px solid rgba(255, 180, 0, 0.4);
  color: #ffdd99;
`;

const AuthActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const GhostButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  padding: 10px 20px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const PrimaryButton = styled(GhostButton)`
  background: linear-gradient(135deg, #ff00aa 0%, #00f5ff 100%);
  border: none;
`;

function CreatePostPage({
  onBack,
  onNavigateToSaved,
  onNavigateToRecommendations,
  onNavigateToCreate,
  onNavigateToMyUploads,
  onNavigateToReview,
  canCreatePost,
  canReview
}) {
  const { user } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [signUpPrefill, setSignUpPrefill] = useState({ name: '', email: '' });

  const topBar = (
    <TopBar>
      <BackButton type="button" onClick={onBack}>
        ← Back to Home
      </BackButton>
      <AuthPanel
        onNavigateToSaved={onNavigateToSaved}
        onNavigateToRecommendations={onNavigateToRecommendations}
        onNavigateToCreate={onNavigateToCreate}
        onNavigateToMyUploads={onNavigateToMyUploads}
        onNavigateToReview={onNavigateToReview}
        canCreatePost={canCreatePost}
        canReview={canReview}
      />
    </TopBar>
  );

  const currentUser = user
    ? {
        uid: user.uid,
        displayName: user.displayName || null,
        email: user.email || null,
        photoURL: user.photoURL || null,
      }
    : null;

  const openSignIn = (mode = 'signup', prefill = {}) => {
    setAuthMode(mode);
    if (mode === 'signup') {
      setSignUpPrefill({
        name: prefill.name || '',
        email: prefill.email || '',
      });
    }
    setAuthModalOpen(true);
  };

  return (
    <Container>
      {topBar}
      <ShareExperienceFlow
        onClose={onBack}
        currentUser={currentUser}
        onRequestSignIn={openSignIn}
      />
      <AuthModal
        open={authModalOpen}
        mode={authMode}
        onClose={() => setAuthModalOpen(false)}
        onModeChange={setAuthMode}
        initialName={signUpPrefill.name}
        initialEmail={signUpPrefill.email}
      />
    </Container>
  );
}

export default CreatePostPage;
